// Updates, in Settings' Endpoints group: the releases reaching this tenant's
// endpoints and the controls over how they spread (a wave schedule, endpoint
// and directory group overrides, and each component's release policy and
// preview track), in three tabs.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import SectionActions from "../components/settings/SectionActions.jsx";
import { PAGE_PART } from "../components/settings/Page.jsx";
import ConfirmDialog from "../components/ConfirmDialog.jsx";
import {
    ARTIFACT_ROLLOUT_PAGE_SIZE,
    applyArtifactRolloutCommand,
    listArtifactRollouts,
    protoBytesToUuid,
    uuidToProtoBytes,
} from "../lib/artifactRolloutClient.js";
import { ENTITLEMENTS, hasEntitlement } from "../lib/entitlements.js";
import { CONTENT_UPDATES, commonValue, groupContentConfigurations, isBundledWatchdog, isContentUpdate } from "../lib/artifactRolloutGroups.js";
import { listDirectoryGroups, listDirectoryIntegrations } from "../lib/identityClient.js";
import { listEndpoints } from "../lib/reportClient.js";
import { hasPermission, useMyPermissions } from "../lib/permissions.js";
import { PERMISSIONS } from "../lib/permissionKeys.js";
import { DIALOG } from "../lib/analytics/surfaceVocabulary.js";
import ContentUpdatesRolloutRow from "./ContentUpdatesRolloutRow.jsx";
import Button, { buttonClass as uiButtonClass } from "../components/ui/Button.jsx";
import Field from "../components/ui/Field.jsx";
import Notice from "../components/ui/Notice.jsx";
import Panel from "../components/ui/Panel.jsx";
import Select from "../components/ui/Select.jsx";
import SearchInput from "../components/ui/SearchInput.jsx";
import { SkeletonBar } from "../components/ui/Skeleton.jsx";
import TableBar from "../components/ui/TableBar.jsx";
import Tabs from "../components/ui/Tabs.jsx";
import { FIELD, ICON_BUTTON } from "../components/ui/styles.js";

/// One page of synced groups is enough to choose from; the note below the
/// picker says so when there are more.
const DIRECTORY_GROUP_PAGE_SIZE = 200;

// Keep each hostname search bounded; users can narrow the search or paste an ID.
const ENDPOINT_PICKER_LIMIT = 100;

/// Sentinel for the escape hatch. Not a UUID, so it cannot collide with one.
const MANUAL_ENDPOINT_ENTRY = "manual";
const MAX_WAVE_POSITION = 32767;

/// Releases first. A reader opening this page is far more often checking what
/// is reaching their endpoints than changing how it gets there.
const TAB_RELEASES = "releases";
const TAB_FLEET = "fleet";
const TAB_COMPONENTS = "components";
// The releases table shows what can start now, or what already ran.
const ROLLOUT_VIEWS = [{ id: "available", label: "Available updates" }, { id: "history", label: "Rollout history" }];

const AUTOMATIC = "ARTIFACT_WAVE_MODE_AUTOMATIC";
const DECLINED = "ARTIFACT_PUBLICATION_STATE_DECLINED";
const GENERAL = "ARTIFACT_AUDIENCE_KIND_GENERAL";
const MANUAL = "ARTIFACT_WAVE_MODE_MANUAL";
const PAUSED = "ARTIFACT_PUBLICATION_STATE_PAUSED";
const PENDING = "ARTIFACT_PUBLICATION_STATE_PENDING";
const ROLLING = "ARTIFACT_PUBLICATION_STATE_ROLLING";
const TENANT_MAIN = "ARTIFACT_AUDIENCE_KIND_TENANT_MAIN";
const TENANT_PREVIEW = "ARTIFACT_AUDIENCE_KIND_TENANT_PREVIEW";
const TENANT_TARGET = "ARTIFACT_AUDIENCE_KIND_TENANT_TARGET";

// An enum value as a table cell reads it: "Tenant main", "Rolling".
function label(value) {
    const words = String(value || "unspecified")
        .replace(/^ARTIFACT_[A-Z]+_(KIND_|STATE_|MODE_|POLICY_)?/, "")
        .replaceAll("_", " ")
        .toLowerCase();
    return words.charAt(0).toUpperCase() + words.slice(1);
}

function directoryGroupLabel(rule) {
    const name = rule.displayName?.trim();
    return name ? `${name} (${rule.directoryGroupId})` : rule.directoryGroupId;
}

function count(value) {
    return String(value ?? "0");
}

const POLICY_AUTOMATIC = "ARTIFACT_PUBLICATION_POLICY_AUTO_ALL";
const POLICY_AUTO_MINOR = "ARTIFACT_PUBLICATION_POLICY_AUTO_MINOR";
const POLICY_MANUAL = "ARTIFACT_PUBLICATION_POLICY_MANUAL";
const POLICY_OPTIONS = [
    { value: POLICY_AUTOMATIC, label: "Start automatically" },
    { value: POLICY_MANUAL, label: "Wait for me" },
];

/// Collapse the stored policy onto the two choices that differ in behaviour.
///
/// The stored enum has three values, but each is read for one release class
/// only: `minor_policy` is consulted for a minor release, where both
/// `auto_all` and `auto_minor` mean "publish automatically", and
/// `major_policy` for a major, where `auto_minor` means the same as `manual`.
/// So per class there are exactly two outcomes, and offering three options
/// asked the reader to choose between two that behave identically.
function policyChoice(policy, releaseClass) {
    if (policy === POLICY_AUTOMATIC) return POLICY_AUTOMATIC;
    if (policy === POLICY_AUTO_MINOR && releaseClass === "minor") return POLICY_AUTOMATIC;
    return POLICY_MANUAL;
}

/// Parse a `percent:seconds` ladder.
///
/// Returns null rather than throwing, because this runs on every keystroke to
/// preview what was typed. The same encoding the operator console accepts, so
/// a ladder can be moved between them verbatim.
const WAVE_UNITS = [
    { label: "seconds", seconds: 1 },
    { label: "minutes", seconds: 60 },
    { label: "hours", seconds: 3600 },
    { label: "days", seconds: 86400 },
];

/// Show a wait in the largest unit that divides it exactly, so 300 reads as
/// 5 minutes and 90 stays 90 seconds rather than becoming 1.5 minutes.
export function secondsToRow(bakeSeconds) {
    const total = Number(bakeSeconds) || 0;
    const unit =
        [...WAVE_UNITS].reverse().find((entry) => total >= entry.seconds && total % entry.seconds === 0) ??
        WAVE_UNITS[0];
    return { amount: String(total / unit.seconds), unit: unit.label };
}

export function rowToSeconds(row) {
    const unit = WAVE_UNITS.find((entry) => entry.label === row.unit) ?? WAVE_UNITS[0];
    return Number(row.amount) * unit.seconds;
}

/// Rows to the wire encoding, so one validator covers both the rows a reader
/// edits and a schedule arriving from the server.
export function rowsToLadder(rows) {
    return (rows ?? [])
        .map((row) => `${String(row.percent).trim()}:${rowToSeconds(row)}`)
        .join(",");
}

export function stepsToRows(steps) {
    return (steps ?? []).map((step) => ({
        percent: String(step.cumulativePercent),
        ...secondsToRow(step.bakeSeconds),
    }));
}

export function parseWaveSteps(text) {
    const parts = String(text ?? "")
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean);
    if (parts.length === 0) return null;
    const steps = [];
    for (const [index, part] of parts.entries()) {
        const match = /^(\d{1,3}):(\d{1,6})$/.exec(part);
        if (!match) return null;
        const cumulativePercent = Number(match[1]);
        const bakeSeconds = Number(match[2]);
        if (cumulativePercent < 1 || cumulativePercent > 100) return null;
        // Cumulative means strictly increasing; a flat or falling step would
        // expose nobody new and never complete.
        if (index > 0 && cumulativePercent <= steps[index - 1].cumulativePercent) return null;
        steps.push({ bakeSeconds, cumulativePercent, position: index + 1 });
    }
    return steps.at(-1).cumulativePercent === 100 ? steps : null;
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/// Enough of an identifier to recognize and correlate, without pretending the
/// whole thing is readable.
function shortId(value) {
    return UUID_PATTERN.test(value) ? `${value.slice(0, 8)}...${value.slice(-4)}` : value;
}

/// Turn a component key into something a person can read.
///
/// The keys are addresses, not names: `agent:agent`, and for the other kinds a
/// bare UUID after the prefix. `displayName` carries the name the component
/// actually has, resolved server-side: a Managed Skill's is the tenant's own,
/// and a content module's is the provider tag from its signed manifest.
/// Without one there is no name to show, so the kind and a shortened
/// identifier remain the honest rendering.
///
/// Content modules share one dashboard entry; Overseer exposes module identity.
function componentLabel(componentKey, componentKind, displayName) {
    if (componentKey === CONTENT_UPDATES) return "Content updates";
    const key = String(componentKey ?? "");
    const separator = key.indexOf(":");
    const suffix = separator === -1 ? key : key.slice(separator + 1);
    const name = String(displayName ?? "").trim();
    switch (componentKind) {
        case "ARTIFACT_COMPONENT_KIND_AGENT":
            return `Origin ${suffix.charAt(0).toUpperCase()}${suffix.slice(1)}`;
        case "ARTIFACT_COMPONENT_KIND_WASM":
            return "Content updates";
        case "ARTIFACT_COMPONENT_KIND_MANAGED_SKILL":
            return `Managed Skill ${name || shortId(suffix)}`;
        default:
            return key || "unknown component";
    }
}

/// Spell a count out with its unit, so "0" reads as a real answer rather than
/// looking like a missing value.
function plural(count, unit) {
    return `${count} ${unit}${count === 1 ? "" : "s"}`;
}

/// Render a bake as a duration a reader can judge, not a raw second count.
///
/// Always a duration, including zero: an earlier version collapsed 0 to "no
/// wait", which the surrounding sentence turned into "then wait no wait".
function bakeLabel(seconds) {
    const total = Number(seconds ?? 0);
    if (!Number.isFinite(total) || total <= 0) return plural(0, "second");
    const whole = Math.trunc(total);
    if (whole < 60) return plural(whole, "second");
    const minutes = Math.floor(whole / 60);
    const remainderSeconds = whole % 60;
    if (minutes < 60) {
        return remainderSeconds
            ? `${plural(minutes, "minute")} ${plural(remainderSeconds, "second")}`
            : plural(minutes, "minute");
    }
    const hours = Math.floor(minutes / 60);
    const remainderMinutes = minutes % 60;
    return remainderMinutes
        ? `${plural(hours, "hour")} ${plural(remainderMinutes, "minute")}`
        : plural(hours, "hour");
}

/// The round-trip encoding the schedule box accepts. Shared with the card's
/// edit button so a loaded schedule is exactly what the editor would emit.
export function formatWaveSteps(steps) {
    return (steps ?? [])
        .map((step) => `${step.cumulativePercent}:${step.bakeSeconds}`)
        .join(",");
}

function stepLabel(step, unit, isLast) {
    const share = `${step.cumulativePercent}% of ${unit}`;
    return `${share}, then wait ${bakeLabel(step.bakeSeconds)} before ${isLast ? "completion" : "advancing"}`;
}

function StepList({ steps, unit }) {
    const list = steps ?? [];
    return (
        <ol className="m-0 grid list-none gap-[2px] p-0">
            {list.map((step, index) => (
                <li key={`${step.cumulativePercent}-${index}`} className="flex gap-[8px]">
                    <span className="tabular-nums text-[color:var(--muted-soft)]">{index + 1}.</span>
                    <span>{stepLabel(step, unit, index === list.length - 1)}</span>
                </li>
            ))}
        </ol>
    );
}

function timestamp(value) {
    if (!value) return "";
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
}

const inputClass = `${FIELD} min-w-0`;
// First-load placeholder bars, one per rollout table column.
const SKELETON_CELLS = ["w-[70%]", "w-[50%]", "w-[60%]", "w-[50%]", "w-[40%]", "w-[50%]", "w-[60%]"];

// The content updates row (ContentUpdatesRolloutRow) draws its own buttons.
const buttonClass = uiButtonClass("secondary", "sm");
// What a part of the page is for, under its heading.
const DESCRIPTION = "m-0 max-w-[72ch] text-[13px] leading-[1.5] text-[color:var(--muted)]";
// The same, inside a panel.
const PANEL_TEXT = "m-0 text-[12.5px] leading-[1.5] text-[color:var(--muted)]";
// A cell's second line: the caveat under a state or a count.
const FOOTNOTE = "text-[11.5px] leading-[1.4] text-[color:var(--muted-soft)]";
// The releases table's header and body cells, in DataTable's look; the
// padding matches ContentUpdatesRolloutRow's cells.
const TH = "px-3 py-2 text-left text-[12px] font-medium leading-[1.2]";
const TD = "px-3 py-2 align-top";

// The gRPC code the command service returns when a tenant lacks the
// `artifact_rollout_control` entitlement: api-server answers
// `Status::permission_denied("artifact rollout control entitlement is
// required")`. Deliberately NOT `FAILED_PRECONDITION`, which is what
// `StateConflict` and an inactive cutover map to, so keying on that would both
// miss every real entitlement refusal and label every ordinary state conflict
// as one.
//
// Still handled even though the controls are now hidden from a tenant known to
// lack the entitlement: the entitlement can be revoked while the page is open,
// after the entitlement was confirmed.
const ENTITLEMENT_REFUSAL_CODE = "PERMISSION_DENIED";

export default function ArtifactRolloutsPage({ entitlements = null }) {
    if (!hasEntitlement(entitlements, ENTITLEMENTS.ARTIFACT_ROLLOUT_CONTROL)) return null;
    return <ArtifactRolloutControls legacyUpdatesBlocked={hasEntitlement(entitlements, ENTITLEMENTS.DISABLE_AUTO_UPDATE)} />;
}

function ArtifactRolloutControls({ legacyUpdatesBlocked }) {
    const caps = useMyPermissions();
    const canControl = hasPermission(caps, PERMISSIONS.TENANT_CONFIG_WRITE);
    const [unentitled, setUnentitled] = useState(false);
    const [data, setData] = useState({
        assignmentRulesTruncated: false,
        configurations: [],
        directoryGroupRules: [],
        endpointRules: [],
        policies: [],
        rollouts: [],
        nextPageToken: "",
        tracks: [],
    });
    const [showHistory, setShowHistory] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    // The change waiting for the reader to confirm it: { kind: "preview" }, or
    // { kind: "endpoint" | "group", rule } for removing an override.
    const [asking, setAsking] = useState(null);
    const savingRef = useRef(false);
    const [componentId, setComponentId] = useState("");
    const [majorPolicy, setMajorPolicy] = useState(POLICY_MANUAL);
    const [minorPolicy, setMinorPolicy] = useState(POLICY_MANUAL);
    const [endpointId, setEndpointId] = useState("");
    const [trackId, setTrackId] = useState("");
    const [wavePosition, setWavePosition] = useState("1");
    const [directoryGroupId, setDirectoryGroupId] = useState("");
    const [directoryIntegrationId, setDirectoryIntegrationId] = useState("");
    // Synced directory data, so the override targets a group by name instead of
    // a pasted UUID. A group that is not synced has no resolvable membership,
    // so the picker is also the accurate set of things a rule can act on.
    const [integrations, setIntegrations] = useState([]);
    const [groups, setGroups] = useState({ items: [], truncated: false });
    const [directoryUnavailable, setDirectoryUnavailable] = useState(false);

    useEffect(() => {
        let active = true;
        void (async () => {
            try {
                const response = await listDirectoryIntegrations();
                if (active) setIntegrations(response?.integrations ?? []);
            } catch {
                // Directory integrations are a separate feature; failing to read
                // them must not take out the rollout page.
                if (active) setDirectoryUnavailable(true);
            }
        })();
        return () => {
            active = false;
        };
    }, []);

    const [endpointOptions, setEndpointOptions] = useState({ items: [], total: 0 });
    const [endpointEntryManual, setEndpointEntryManual] = useState(false);
    const [endpointQuery, setEndpointQuery] = useState("");
    const [endpointLoading, setEndpointLoading] = useState(false);
    const [endpointError, setEndpointError] = useState("");
    // "" is the tenant-wide schedule; a component id scopes the editor to one
    // component, which outranks the tenant-wide one for that component only.
    const [scheduleScope, setScheduleScope] = useState("");
    const [waveRows, setWaveRows] = useState([]);
    const [pendingConfirm, setPendingConfirm] = useState(null);
    // The select shows the manual sentinel while a typed id is in play, so the
    // two controls never disagree about what is selected.
    const endpointPickerValue = endpointEntryManual ? MANUAL_ENDPOINT_ENTRY : endpointId;

    useEffect(() => {
        let active = true;
        setEndpointLoading(true);
        setEndpointError("");
        const search = async () => {
            try {
                const response = await listEndpoints({ page: 0, pageSize: ENDPOINT_PICKER_LIMIT, hostname: endpointQuery.trim() || undefined });
                if (!active) return;
                setEndpointOptions({
                    items: (response?.endpoints ?? []).map((endpoint) => ({
                        ...endpoint,
                        id: protoBytesToUuid(endpoint.id),
                    })),
                    total: response?.total ?? 0,
                });
            } catch {
                if (active) {
                    setEndpointOptions({ items: [], total: 0 });
                    setEndpointError("Endpoints could not be loaded. Change the search to retry, or enter an ID manually.");
                }
            } finally {
                if (active) setEndpointLoading(false);
            }
        };
        const timer = endpointQuery ? setTimeout(search, 300) : null;
        if (!endpointQuery) void search();
        return () => {
            active = false;
            clearTimeout(timer);
        };
    }, [endpointQuery]);

    useEffect(() => {
        if (!directoryIntegrationId) {
            setGroups({ items: [], truncated: false });
            return undefined;
        }
        let active = true;
        void (async () => {
            try {
                const response = await listDirectoryGroups({
                    integration_id: directoryIntegrationId,
                    page_size: DIRECTORY_GROUP_PAGE_SIZE,
                });
                if (!active) return;
                setGroups({
                    items: response?.groups ?? [],
                    truncated: Boolean(response?.nextPageToken),
                });
            } catch {
                if (active) setGroups({ items: [], truncated: false });
            }
        })();
        return () => {
            active = false;
        };
    }, [directoryIntegrationId]);

    const refresh = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const response = await listArtifactRollouts();
            setData({
                assignmentRulesTruncated: response?.assignmentRulesTruncated ?? false,
                configurations: response?.configurations ?? [],
                directoryGroupRules: response?.directoryGroupRules ?? [],
                endpointRules: response?.endpointRules ?? [],
                policies: response?.policies ?? [],
                rollouts: response?.rollouts ?? [],
                nextPageToken: response?.nextPageToken ?? "",
                tracks: response?.tracks ?? [],
            });
        } catch (cause) {
            setError(cause?.serverMessage || cause?.message || String(cause));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void refresh();
    }, [refresh]);

    const [loadingMore, setLoadingMore] = useState(false);
    // Only the rollout list is cursor-paged; every sibling list in the response
    // is capped server-side, so a later page must not replace them.
    const loadMore = useCallback(async () => {
        if (!data.nextPageToken) return;
        setLoadingMore(true);
        setError("");
        try {
            const response = await listArtifactRollouts({ pageToken: data.nextPageToken });
            setData((current) => ({
                ...current,
                rollouts: [...current.rollouts, ...(response?.rollouts ?? [])],
                nextPageToken: response?.nextPageToken ?? "",
            }));
        } catch (cause) {
            setError(cause?.serverMessage || cause?.message || String(cause));
        } finally {
            setLoadingMore(false);
        }
    }, [data.nextPageToken]);

    // `value` may be a builder rather than a value. `uuidToProtoBytes` throws
    // on a malformed UUID, and when the argument object was built at the call
    // site that throw happened before this function was entered, so the catch
    // below never saw it: the operator got a dead button and no message.
    // Building inside the try puts input validation on the same error path as
    // the request.
    const run = useCallback(async (name, value) => {
        if (savingRef.current) return;
        savingRef.current = true;
        setSaving(true);
        setError("");
        setUnentitled(false);
        let completed = 0;
        let total = 1;
        try {
            const argument = typeof value === "function" ? value() : value;
            const commands = Array.isArray(argument) ? argument : [argument];
            total = commands.length;
            for (const command of commands) {
                await applyArtifactRolloutCommand(typeof name === "function" ? name(command) : name, command);
                completed += 1;
            }
            await refresh();
        } catch (cause) {
            if (completed) await refresh();
            if (completed) setError(`${completed} of ${total} changes saved. Review the current settings before retrying.`);
            // The server is the entitlement authority. Every command here
            // except the Managed Skill ones needs `artifact_rollout_control`,
            // and TENANT_CONFIG_WRITE alone does not imply it. The controls are
            // hidden until the entitlement is confirmed, but it can still be
            // revoked after the page mounts.
            //
            // Keyed on the gRPC code, not the message: the refusal text is
            // English prose that can be reworded without this page's tests
            // noticing, which would leave an unentitled tenant staring at a
            // raw error string instead of the reason.
            if (cause?.grpcCode === ENTITLEMENT_REFUSAL_CODE) {
                setUnentitled(true);
                return;
            }
            const detail = cause?.serverMessage || cause?.message || String(cause);
            setError(total > 1 ? `${completed} of ${total} changes saved. Review the current settings before retrying. ${detail}` : detail);
        } finally {
            savingRef.current = false;
            setSaving(false);
        }
    }, [refresh]);

    const configurations = useMemo(() => groupContentConfigurations(data.configurations), [data.configurations]);
    const contentComponentIds = useMemo(() => [...new Set(
        [...data.configurations, ...data.rollouts].filter(isContentUpdate).map((entry) => entry.componentId),
    )].sort(), [data.configurations, data.rollouts]);
    const scopeIds = useCallback((scope) => scope === CONTENT_UPDATES ? contentComponentIds : [scope], [contentComponentIds]);

    const components = useMemo(() => {
        const unique = new Map();
        for (const rollout of [...data.configurations, ...data.rollouts]) {
            if (isBundledWatchdog(rollout)) continue;
            unique.set(
                isContentUpdate(rollout) ? CONTENT_UPDATES : rollout.componentId,
                componentLabel(rollout.componentKey, rollout.componentKind, rollout.displayName)
                    || protoBytesToUuid(rollout.componentId),
            );
        }
        return [...unique.entries()].sort((left, right) => left[1].localeCompare(right[1]));
    }, [data.configurations, data.rollouts]);

    const previewTrack = data.tracks.find((track) => track.trackKind === "ARTIFACT_TRACK_KIND_PREVIEW");
    const previewEnabled = Boolean(componentId && scopeIds(componentId).every((id) => previewTrack?.enabledComponentIds?.includes(id)));
    const previewMixed = componentId === CONTENT_UPDATES && !previewEnabled && contentComponentIds.some((id) => previewTrack?.enabledComponentIds?.includes(id));

    // Without this the selects always showed their defaults, so opening the page
    // and pressing Save quietly replaced a configured policy with those
    // defaults. A control that misreports the current setting is worse than no
    // control.
    useEffect(() => {
        if (!componentId) return;
        const saved = scopeIds(componentId).map((id) => data.policies.find((policy) => policy.componentId === id));
        setMajorPolicy(commonValue(saved.map((policy) => policyChoice(policy?.majorPolicy, "major"))));
        setMinorPolicy(commonValue(saved.map((policy) => policyChoice(policy?.minorPolicy, "minor"))));
    }, [componentId, data.policies, scopeIds]);

    // Seed from the ladder in force. A tenant ladder applies to every
    // component, so once one is set they all resolve to it and the set is
    // uniform; a mixed set means this tenant is still on Origin's
    // per-component defaults, which is worth saying rather than picking one
    // arbitrarily and calling it "current".
    const ladders = useMemo(
        () =>
            data.configurations.filter((entry) => !isBundledWatchdog(entry)).map((entry) => formatWaveSteps(entry.waveSteps)),
        [data.configurations],
    );
    const laddersAgree = ladders.length > 0 && ladders.every((entry) => entry === ladders[0]);

    // Seeding is driven by fresh data and by explicit actions, never by the
    // scope state itself. Depending on `scheduleScope` meant an action that
    // set the scope and the rows together had its rows overwritten by this
    // effect a moment later: pressing Edit on one card while scoped to
    // another emptied the editor instead of loading that card.
    const scopeRef = useRef("");
    const seedRowsFor = useCallback(
        (scope) => {
            scopeRef.current = scope;
            if (scope) {
                const scoped = configurations.find(
                    (entry) => entry.componentId === scope,
                );
                setWaveRows(stepsToRows(scoped?.waveSteps));
                return;
            }
            setWaveRows(
                laddersAgree ? stepsToRows(data.configurations.find((entry) => !isBundledWatchdog(entry))?.waveSteps) : [],
            );
        },
        [configurations, data.configurations, laddersAgree],
    );

    useEffect(() => {
        seedRowsFor(scopeRef.current);
    }, [seedRowsFor]);

    const [groupTrackId, setGroupTrackId] = useState("");
    const [groupWavePosition, setGroupWavePosition] = useState("1");
    const [tab, setTab] = useState(TAB_RELEASES);
    const editorRef = useRef(null);

    // The editor sits above the cards, so filling it from a card below is a
    // change the reader cannot see. Bring it into view as well as filling it.
    const editScheduleRows = useCallback(
        (configuration, scoped) => {
            const scope = scoped ? configuration.componentId : "";
            setScheduleScope(scope);
            scopeRef.current = scope;
            // Seeded from the card that was pressed, which for an unscoped
            // edit is not what the scope alone would produce.
            setWaveRows(stepsToRows(configuration.waveSteps));
            editorRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
        },
        [],
    );

    const changeScheduleScope = useCallback(
        (scope) => {
            setScheduleScope(scope);
            seedRowsFor(scope);
        },
        [seedRowsFor],
    );

    const setRow = useCallback((index, patch) => {
        setWaveRows((rows) => rows.map((row, at) => (at === index ? { ...row, ...patch } : row)));
    }, []);
    const removeRow = useCallback((index) => {
        setWaveRows((rows) => rows.filter((_, at) => at !== index));
    }, []);
    // A new wave starts at 100 because the last one has to, and a reader
    // adding a step is usually inserting before the finish rather than
    // extending past it.
    const addRow = useCallback(() => {
        setWaveRows((rows) => [...rows, { amount: "0", percent: "100", unit: "seconds" }]);
    }, []);

    // The per-component tab is entirely write controls, so it has nothing to
    // show an operator without write permission. The fleet tab still shows
    // the schedule in force and the existing rules to entitled readers.
    const tabs = useMemo(
        () => [
            { id: TAB_RELEASES, label: "Releases" },
            { id: TAB_FLEET, label: "Your fleet" },
            ...(canControl ? [{ id: TAB_COMPONENTS, label: "Per component" }] : []),
        ],
        [canControl],
    );

    const visibleRollouts = data.rollouts.filter((rollout) => {
        if (isBundledWatchdog(rollout)) return false;
        const active = rollout.audienceKind !== GENERAL && [PENDING, DECLINED, ROLLING, PAUSED].includes(rollout.publicationState);
        return showHistory ? !active : active;
    });

    const contentRollouts = visibleRollouts.filter(isContentUpdate);
    const parsedLadder = parseWaveSteps(rowsToLadder(waveRows));

    const shortestLadder = useMemo(() => {
        let shortest = null;
        for (const entry of data.configurations) {
            const steps = entry.waveSteps ?? [];
            if (steps.length > 0 && (shortest === null || steps.length < shortest.length)) {
                shortest = steps;
            }
        }
        return shortest;
    }, [data.configurations]);
    const isWavePosition = (value) =>
        Number.isInteger(Number(value)) && Number(value) >= 1 && Number(value) <= MAX_WAVE_POSITION;
    const wavePositionValid = isWavePosition(wavePosition);
    const groupWavePositionValid = isWavePosition(groupWavePosition);
    // The page starts with no rows when components disagree, so reporting that
    // as invalid input would blame the reader for something they never typed.
    const ladderHint = parsedLadder
        ? `${plural(parsedLadder.length, "wave")}. ${parsedLadder
              .map((step, index) => stepLabel(step, "your endpoints", index === parsedLadder.length - 1))
              .join("; ")}`
        : waveRows.length === 0
          ? ladders.length > 0 && !laddersAgree
              ? "Your components are on different schedules, so there is no single one to start from. Add waves here, or use Edit this schedule on any component below to load its schedule."
              : "Add a wave to start."
          : "Not a schedule yet. Each share must be larger than the one above it, and the last must be 100%.";

    // Saving moves every component onto one schedule, so it names the ones
    // that are on a different one first. Re-saving what is already in force
    // moves nothing and asks nothing.
    const saveCommand = (steps) =>
        scheduleScope
            ? scopeIds(scheduleScope).map((id) => ({ componentId: id, name: "component", steps }))
            : { name: "tenant", steps };

    const saveWaveLadder = () => {
        if (!parsedLadder) return;
        const ladder = rowsToLadder(waveRows);
        const changes = (entry) => formatWaveSteps(entry.waveSteps) !== ladder;
        // A component on its own schedule does not move when the tenant-wide
        // one is saved, so naming it would be a false warning.
        const moved = [...new Set(data.configurations
            .filter((entry) =>
                scheduleScope
                    ? scopeIds(scheduleScope).includes(entry.componentId)
                    : entry.waveScope !== "tenant_component",
            )
            .filter(changes)
            .map((entry) =>
                componentLabel(entry.componentKey, entry.componentKind, entry.displayName),
            ))];
        // One component changing is the reader's own explicit choice, so it
        // needs no warning; a tenant-wide save reaching several does.
        if (moved.length === 0 || scheduleScope) {
            void run("setWaveProfile", saveCommand(parsedLadder));
            return;
        }
        setPendingConfirm({ kind: "save", moved, steps: parsedLadder });
    };

    // Components that follow the tenant-wide schedule are the ones removing it
    // moves; a component with its own keeps it, and one already on an Origin
    // default is already where this would put it.
    const followsGlobalSchedule = data.configurations.filter(
        (entry) => entry.waveScope === "tenant",
    );

    const clearGlobalSchedule = () =>
        setPendingConfirm({
            kind: "clear",
            moved: [...new Set(followsGlobalSchedule.map((entry) =>
                componentLabel(entry.componentKey, entry.componentKind, entry.displayName),
            ))],
        });

    const confirmPendingAction = () => {
        const pending = pendingConfirm;
        setPendingConfirm(null);
        if (!pending) return;
        if (pending.kind === "clear") {
            // No component: the tenant-wide profile itself.
            void run("clearWaveProfile", {});
            return;
        }
        void run("setWaveProfile", saveCommand(pending.steps));
    };

    const useGlobalSchedule = (componentId) =>
        run("clearWaveProfile", scopeIds(componentId)
            .filter((id) => data.configurations.some((entry) => entry.componentId === id && entry.waveScope === "tenant_component"))
            .map((id) => ({ componentId: id })));

    const setPolicy = () => run("setPublicationPolicy", scopeIds(componentId).map((id) => ({
        componentId: id,
        majorPolicy,
        minorPolicy,
    })));

    const setPreview = (enabled) => {
        setAsking(null);
        void run("enablePreviewTrack", scopeIds(componentId).map((id) => ({ componentId: id, enabled })));
    };
    // Turning preview off asks first.
    const togglePreview = () => {
        if (!componentId) return;
        if (previewEnabled) setAsking({ kind: "preview" });
        else setPreview(true);
    };

    const setEndpointTrack = () => run("setEndpointTrack", () => ({
        endpointId: uuidToProtoBytes(endpointId),
        trackId: trackId || undefined,
    }));

    const setEndpointWave = () => run("setEndpointWave", () => ({
        endpointId: uuidToProtoBytes(endpointId),
        wavePosition: Number(wavePosition),
    }));

    const setDirectoryGroupTrack = () => run("setDirectoryGroupTrack", () => ({
        directoryGroupId,
        directoryIntegrationId,
        trackId: groupTrackId || undefined,
    }));

    const setDirectoryGroupWave = () => run("setDirectoryGroupWave", () => ({
        directoryGroupId,
        directoryIntegrationId,
        wavePosition: Number(groupWavePosition),
    }));

    const overrideName = (pending) => (pending.kind === "endpoint"
        ? pending.rule.hostname || protoBytesToUuid(pending.rule.endpointId)
        : directoryGroupLabel(pending.rule));
    const removingOverride = asking?.kind === "endpoint" || asking?.kind === "group" ? asking : null;
    const removeOverride = () => {
        const pending = removingOverride;
        setAsking(null);
        if (!pending) return;
        if (pending.kind === "endpoint") void run("clearEndpointOverrides", { endpointId: pending.rule.endpointId });
        else void run("clearDirectoryGroupOverrides", { directoryGroupId: pending.rule.directoryGroupId, directoryIntegrationId: pending.rule.directoryIntegrationId });
    };
    const endpointHint = endpointLoading || endpointError
        ? null
        : endpointQuery && endpointOptions.total === 0
            ? "No endpoints match this hostname."
            : endpointOptions.total > endpointOptions.items.length
                ? `Showing the first ${endpointOptions.items.length} of ${endpointOptions.total} endpoints. Narrow the hostname search or enter an ID manually.`
                : null;
    const wavePositionError = `Enter a whole wave number from 1 to ${MAX_WAVE_POSITION}.`;
    const trackOptions = data.tracks.map((track) => ({ value: track.trackId, label: track.name }));

    return (
        <>
            <SectionActions>
                <Button className="shrink-0" onClick={() => void refresh()}>Refresh</Button>
            </SectionActions>
            <div className="min-w-0">
                <fieldset disabled={saving} aria-busy={loading || saving} className="m-0 grid min-w-0 gap-[18px] border-0 p-0">
                    {saving && <p role="status" className="m-0 text-[12.5px] text-[color:var(--muted)]">Saving changes…</p>}

                    {legacyUpdatesBlocked && (
                        <Notice tone="warning" title="Agent updates are blocked by your legacy update setting.">
                            Before removing the block, your Origin administrator must confirm that existing releases require your approval.
                        </Notice>
                    )}

                    {error && <Notice tone="error">{error}</Notice>}

                    {unentitled && (
                        <Notice tone="warning" title="Release rollout controls need the artifact rollout control entitlement for this tenant.">
                            Managed Skill deployments are unaffected. Contact your Origin representative to enable it.
                        </Notice>
                    )}

                    <Tabs items={tabs} value={tab} onChange={setTab} label="Update settings" className="justify-self-start" />

                    {tab === TAB_RELEASES && (
                        <div className="grid min-w-0 gap-[10px]">
                            <p className={DESCRIPTION}>
                                Choose the version you want to start. Releases waiting for approval do not reach your endpoints.
                                Starting a version opens its first wave; later versions still need their own approval when your
                                release policy is manual. Pause stops new waves. Hold also stops delivery to endpoints already
                                admitted. Neither removes software already installed.
                            </p>
                            <TableBar>
                                <Tabs
                                    mode="toggle"
                                    label="Releases shown"
                                    items={ROLLOUT_VIEWS}
                                    value={showHistory ? "history" : "available"}
                                    onChange={(id) => setShowHistory(id === "history")}
                                />
                            </TableBar>

                            <div className="min-w-0 overflow-x-auto" aria-busy={loading ? "true" : undefined}>
                                <table className="w-full min-w-[980px] border-collapse text-left text-[12.5px] leading-[1.4]">
                                    <thead className="bg-[color:var(--bg)] text-[color:var(--muted)]">
                                        <tr className="border-b border-[color:var(--line-soft)]">
                                            <th className={TH}>Component</th>
                                            <th className={TH}>Version</th>
                                            <th className={TH}>Audience</th>
                                            <th className={TH}>State</th>
                                            <th className={TH} title="Wave eligibility, not installed versions">Endpoints eligible / active</th>
                                            <th className={TH} title="How waves advance after you start a release">Wave mode</th>
                                            <th className={TH}>Actions</th>
                                        </tr>
                                    </thead>
                                    {/* A refresh dims the rows already on screen rather than
                                        blanking them or adding a block beneath. */}
                                    <tbody
                                        className="divide-y divide-[color:var(--line-soft)]"
                                        style={{ opacity: loading && visibleRollouts.length ? 0.4 : 1, transition: "opacity 180ms ease" }}
                                    >
                                        {contentRollouts.length > 0 && <ContentUpdatesRolloutRow allRows={data.rollouts.filter(isContentUpdate)} buttonClass={buttonClass} canControl={canControl && !showHistory} onRun={run} rows={contentRollouts} />}
                                        {visibleRollouts.filter((rollout) => !isContentUpdate(rollout)).map((rollout) => {
                                            const state = rollout.publicationState;
                                            const publicationId = rollout.publicationId;
                                            const releaseId = rollout.releaseId;
                                            const manual = rollout.waveMode === MANUAL;
                                            return (
                                                <tr key={publicationId} className="transition-colors hover:bg-[color:var(--control-bg)]">
                                                    <td className={`${TD} text-[color:var(--ink)]`}>
                                                        {componentLabel(rollout.componentKey, rollout.componentKind, rollout.displayName)}
                                                        <div className={`${FOOTNOTE} font-mono`} title={rollout.componentKey}>{rollout.componentKey}</div>
                                                    </td>
                                                    <td className={`${TD} font-mono text-[12px] text-[color:var(--ink)]`}>{rollout.version}</td>
                                                    <td className={`${TD} text-[color:var(--muted)]`}>{rollout.audienceKind === GENERAL ? "Origin general rollout" : label(rollout.audienceKind)}</td>
                                                    <td className={`${TD} text-[color:var(--muted)]`}>
                                                        {state === PENDING && rollout.audienceKind === TENANT_MAIN && !rollout.awaitingGeneralCompletion
                                                            ? "Waiting for your approval"
                                                            : label(state)}
                                                        {rollout.held && <div className="font-medium text-[color:var(--ink)]">Delivery blocked by a hold</div>}
                                                        {rollout.audienceKind === GENERAL && <div className={FOOTNOTE}>Origin's rollout status. This does not confirm installation on your endpoints.</div>}
                                                        {rollout.awaitingGeneralCompletion && <div className={FOOTNOTE}>Origin is still rolling this release out generally. It can be started here once that finishes.</div>}
                                                        {state === DECLINED && <div className={FOOTNOTE}>Declined. Start it later to take it anyway.</div>}
                                                        {/* Says which controls stop applying rather than all of them:
                                                            a forced replacement crosses the fleet on the tenant's own
                                                            wave schedule, so claiming their controls do not apply was
                                                            both alarming and wrong. */}
                                                        {rollout.systemEnforced && <div className={FOOTNOTE}>Required support update, because {rollout.replacedVersion ? `${rollout.replacedVersion} is` : "the release you were on is"} no longer supported. This one cannot be declined or held, but it still follows your wave schedule.</div>}
                                                        {rollout.retirementDeadline && <div className={FOOTNOTE}>Retirement due {timestamp(rollout.retirementDeadline)}</div>}
                                                        {/* Without this a tenant watches a retirement date pass and
                                                            nothing happen. Origin will not retire a release until a
                                                            newer one covers everything it does, which for a component
                                                            that never gets a successor is forever. */}
                                                        {rollout.retirementBlocked && <div className={FOOTNOTE}>Staying in place past that date. Origin retires a release only once a newer one covering everything this one does has finished rolling out.</div>}
                                                    </td>
                                                    <td className={`${TD} tabular-nums text-[color:var(--muted)]`}>
                                                        {[PENDING, DECLINED].includes(state) ? "Not started" : <>
                                                            {count(rollout.eligibleEndpointCount)} / {count(rollout.activeEndpointCount)}
                                                            <div className={FOOTNOTE}>Position {rollout.currentPosition ?? 0}</div>
                                                        </>}
                                                        {rollout.componentKind === "ARTIFACT_COMPONENT_KIND_MANAGED_SKILL" && <div className={FOOTNOTE}>{count(rollout.appliedEndpointCount)} applied</div>}
                                                    </td>
                                                    <td className={`${TD} text-[color:var(--muted)]`}>{label(rollout.waveMode)}</td>
                                                    <td className={TD}>
                                                        {canControl && rollout.audienceKind !== GENERAL && !rollout.systemEnforced && (
                                                            <div className="flex flex-wrap gap-[4px]">
                                                                {/* Main only. A preview track follows the latest promoted
                                                                    release on its own: enabling it creates the publication and
                                                                    the scheduler starts it with no policy gate, so there is
                                                                    nothing here for an operator to start. Rendering Start on a
                                                                    preview row pointed it at the main-release command. */}
                                                                {/* A declined row is revivable: `publish_release` short-circuits
                                                                    only on rolling, paused and completed, so Start on a declined
                                                                    publication restarts it. Withholding the button here made a
                                                                    decline look permanent when it is not.

                                                                    Start is withheld while Origin's general rollout is unfinished,
                                                                    because the same publish call refuses that outright. */}
                                                                {[PENDING, DECLINED].includes(state) && rollout.audienceKind === TENANT_MAIN && !rollout.awaitingGeneralCompletion && <Button size="sm" onClick={() => void run("publishTenantMainRelease", { releaseId, waveMode: AUTOMATIC })}>Start</Button>}
                                                                {state === PENDING && <Button size="sm" onClick={() => void run("declinePublication", { publicationId })}>Decline</Button>}
                                                                {(state === ROLLING || state === PAUSED) && <Button size="sm" onClick={() => void run(state === PAUSED ? "resumePublication" : "pausePublication", { publicationId })}>{state === PAUSED ? "Resume" : "Pause"}</Button>}
                                                                {(state === ROLLING || state === PAUSED) && <Button size="sm" onClick={() => void run("setWaveMode", { publicationId, waveMode: manual ? AUTOMATIC : MANUAL })}>{manual ? "Automatic" : "Manual"}</Button>}
                                                                {rollout.holdClearPending && <span className="self-center text-[12px] text-[color:var(--muted)]">Release hold clear pending</span>}
                                                                {[TENANT_MAIN, TENANT_PREVIEW, TENANT_TARGET].includes(rollout.audienceKind) && <Button size="sm" onClick={() => void run(rollout.audienceKind === TENANT_TARGET ? "setTenantHold" : "setTrackHold", {
                                                                    held: !rollout.held,
                                                                    publicationId,
                                                                    reason: rollout.held ? "Tenant cleared release hold" : "Tenant held release rollout",
                                                                    ...(rollout.audienceKind === TENANT_TARGET ? {} : { trackId: rollout.trackId }),
                                                                })}>{rollout.held ? "Clear hold" : "Hold"}</Button>}
                                                                {manual && (rollout.rolloutIds ?? []).map((rolloutId, index) => <Button key={rolloutId} size="sm" onClick={() => void run("advanceWave", { rolloutId })}>Advance lane {index + 1}</Button>)}
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {loading && visibleRollouts.length === 0 && Array.from({ length: 3 }, (_, row) => (
                                            <tr key={`skeleton-${row}`} style={{ opacity: Math.max(0.25, 1 - row * 0.12) }}>
                                                {SKELETON_CELLS.map((width, cell) => (
                                                    <td key={cell} className="px-3 py-3">
                                                        {row === 0 && cell === 0 ? <span className="sr-only">Loading rollout state…</span> : null}
                                                        <SkeletonBar className={width} />
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                        {!loading && visibleRollouts.length === 0 && (
                                            <tr>
                                                <td colSpan="7" className="px-3 py-[14px] text-[13px] text-[color:var(--muted)]">
                                                    {showHistory
                                                        ? "No rollout history in the loaded releases."
                                                        : "No updates awaiting approval or in progress in the loaded releases. Existing general approvals are shown in Rollout history."}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            {data.nextPageToken && (
                                <div className="grid place-items-center pb-[10px] pt-[2px]">
                                    <Button size="sm" onClick={() => void loadMore()} disabled={loadingMore}>
                                        {loadingMore ? "Loading" : `Load ${ARTIFACT_ROLLOUT_PAGE_SIZE} more`}
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}

                    {tab === TAB_FLEET && (
                        <div className="grid min-w-0 gap-[32px]">
                            <section className="grid max-w-[640px] gap-[14px]">
                                <div className="grid gap-[4px]">
                                    <h3 className={PAGE_PART}>Your global wave schedule</h3>
                                    <p className={DESCRIPTION}>
                                        How quickly a release spreads across your endpoints once it reaches you. Every
                                        component uses this one schedule, so saving it changes the pace for Agent,
                                        Content Updates and Managed Skills together. Each wave reaches a share of your
                                        endpoints and then waits before the next one opens; the wait is an observation
                                        window, not a health check. The last wave must reach 100%, and its wait is the
                                        final observation period before the release is complete. Saving affects
                                        releases published afterwards, not anything already moving.
                                    </p>
                                </div>
                                <Field
                                    label="Applies to"
                                    hint={scheduleScope === CONTENT_UPDATES ? `Applies to all ${contentComponentIds.length} listed content modules. New versions keep this schedule; updates already moving keep their current schedule.` : null}
                                >
                                    <div className="max-w-[420px]">
                                        <Select
                                            ariaLabel="Schedule applies to"
                                            value={scheduleScope}
                                            onChange={changeScheduleScope}
                                            options={[
                                                { value: "", label: "Every component" },
                                                ...configurations.map((entry) => ({
                                                    value: entry.componentId,
                                                    label: `${componentLabel(entry.componentKey, entry.componentKind, entry.displayName)} only`,
                                                })),
                                            ]}
                                        />
                                    </div>
                                </Field>
                                <div ref={editorRef} className="grid gap-[6px]">
                                    <div className="grid grid-cols-[96px_minmax(0,1fr)_28px] items-center gap-[8px] text-[12.5px] font-medium text-[color:var(--ink)]">
                                        <span>Reaches</span>
                                        <span>Then waits</span>
                                        <span />
                                    </div>
                                    {waveRows.map((row, index) => (
                                        <div key={index} className="grid grid-cols-[96px_minmax(0,1fr)_28px] items-center gap-[8px]">
                                            <div className="flex items-center gap-[6px]">
                                                <input
                                                    className={`${inputClass} w-full`}
                                                    type="number"
                                                    min="1"
                                                    max="100"
                                                    value={row.percent}
                                                    aria-label={`Wave ${index + 1} share of endpoints`}
                                                    onChange={(event) => setRow(index, { percent: event.target.value })}
                                                />
                                                <span className="text-[12.5px] text-[color:var(--muted)]">%</span>
                                            </div>
                                            <div className="flex items-center gap-[6px]">
                                                <input
                                                    className={`${inputClass} w-[80px]`}
                                                    type="number"
                                                    min="0"
                                                    value={row.amount}
                                                    aria-label={`Wave ${index + 1} wait`}
                                                    onChange={(event) => setRow(index, { amount: event.target.value })}
                                                />
                                                <Select
                                                    value={row.unit}
                                                    ariaLabel={`Wave ${index + 1} wait unit`}
                                                    onChange={(unit) => setRow(index, { unit })}
                                                    options={WAVE_UNITS.map((unit) => ({ value: unit.label, label: unit.label }))}
                                                    triggerStyle={{ width: "auto" }}
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                className={ICON_BUTTON}
                                                aria-label={`Remove wave ${index + 1}`}
                                                onClick={() => removeRow(index)}
                                            >
                                                <XMarkIcon aria-hidden="true" className="h-[14px] w-[14px]" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                <p className="m-0 text-[12.5px] leading-[1.45] text-[color:var(--muted)]">
                                    {ladderHint}
                                </p>
                                <div className="flex flex-wrap items-center justify-between gap-[8px]">
                                    <Button onClick={addRow}>Add wave</Button>
                                    <div className="flex flex-wrap items-center gap-[8px]">
                                        {/* Only in the global scope: scoped to one component, the
                                            way back is that component's own card. */}
                                        {!scheduleScope && followsGlobalSchedule.length > 0 && (
                                            <Button onClick={clearGlobalSchedule}>Use Origin's defaults</Button>
                                        )}
                                        <Button variant="primary" disabled={!parsedLadder} onClick={saveWaveLadder}>
                                            Save schedule
                                        </Button>
                                    </div>
                                </div>
                            </section>

                            {data.configurations.length > 0 && (
                                <section className="grid min-w-0 gap-[14px]">
                                    <div className="grid gap-[6px]">
                                        <h3 className={PAGE_PART}>Rollout schedule</h3>
                                        <p className={DESCRIPTION}>
                                            A release reaches the fleet in two stages. Origin widens it across tenants first,
                                            on its own schedule; once it reaches yours, the waves below widen it across your
                                            endpoints. Each endpoint's place in line is drawn from its ID and the release, so
                                            every release reshuffles the order and no endpoint is always first. The wait after a
                                            step is an observation window, not a health check: a stage advances when its time is
                                            up, whether or not endpoints have reported.
                                        </p>
                                        <p className={DESCRIPTION}>
                                            The first stage is Origin's, because it moves a release across every tenant. Waves
                                            are yours: they move it across your endpoints only, and only once Origin has
                                            finished rolling the release out everywhere else, so slowing yours down holds
                                            nobody else up.
                                        </p>
                                    </div>
                                    <div className="grid gap-[10px] lg:grid-cols-2">
                                        {configurations.map((configuration) => (
                                            <Panel
                                                key={configuration.componentId}
                                                title={<span title={configuration.componentKey}>{componentLabel(configuration.componentKey, configuration.componentKind, configuration.displayName)}</span>}
                                                meta={configuration.environment}
                                            >
                                                <div className="grid gap-[6px] text-[12.5px] leading-[1.45] text-[color:var(--muted)]">
                                                    <span className="text-[color:var(--muted-soft)]">Waves, across your endpoints:</span>
                                                    {configuration.mixedWaveSteps
                                                        ? <p className="m-0">Content updates currently use different schedules. Choose one shared schedule above.</p>
                                                        : <StepList steps={configuration.waveSteps} unit="endpoints" />}
                                                    <span className="text-[color:var(--muted-soft)]">
                                                        {configuration.members && !configuration.waveScope
                                                            ? "Using different schedule settings"
                                                            : configuration.waveScope === "tenant_component"
                                                            ? "Using a schedule set for this component"
                                                            : configuration.waveScope === "tenant"
                                                              ? "Using your schedule"
                                                              : "Using Origin's default"}
                                                    </span>
                                                    {configuration.maximumAgeDays > 0 && (
                                                        <p className="m-0">
                                                            <span className="text-[color:var(--muted-soft)]">Support window:</span> retired after {configuration.maximumAgeDays} days or {configuration.maximumNewerReleases} newer releases, whichever comes first
                                                        </p>
                                                    )}
                                                </div>
                                                {/* The editor above writes one schedule for every
                                                    component, so Edit loads a starting point rather
                                                    than editing this component alone. Without it a
                                                    reader looking at the schedule they want to change
                                                    had nothing to click, and when components disagree
                                                    the editor starts empty with nothing to copy. */}
                                                {canControl && (
                                                    <div className="flex flex-wrap gap-[6px] empty:hidden">
                                                        {(configuration.waveScope === "tenant_component" || configuration.members?.some((entry) => entry.waveScope === "tenant_component")) && (
                                                            <Button size="sm" onClick={() => void useGlobalSchedule(configuration.componentId)}>Use the global schedule</Button>
                                                        )}
                                                        {(configuration.waveSteps ?? []).length > 0 && (
                                                            <Button size="sm" onClick={() => editScheduleRows(configuration, false)}>Edit this schedule</Button>
                                                        )}
                                                        {(configuration.waveSteps ?? []).length > 0 && (
                                                            <Button size="sm" onClick={() => editScheduleRows(configuration, true)}>Give this component its own schedule</Button>
                                                        )}
                                                    </div>
                                                )}
                                            </Panel>
                                        ))}
                                    </div>
                                </section>
                            )}

                            <div className="grid min-w-0 gap-[10px] lg:grid-cols-2">
                                <Panel title="Endpoint override" className="content-start">
                                    <p className={PANEL_TEXT}>
                                        Pin one endpoint to a track, or to a fixed place in the wave order. Without an
                                        override an endpoint's place is drawn from its ID and reshuffles every
                                        release; with one it is fixed, so a machine you watch closely can always go
                                        first and a fragile one can always go last.
                                    </p>
                                    <p className={PANEL_TEXT}>
                                        Wave number is a position in the schedule above, counting from 1, not a
                                        percentage: an endpoint set to 2 joins when the second wave opens. The
                                        position applies to every component, not one of them.
                                        {" "}If a component has fewer waves, the endpoint joins its final wave.
                                    </p>
                                    <SearchInput label="Search hostname" placeholder="Search endpoints by hostname" value={endpointQuery} onChange={(value) => {
                                        setEndpointQuery(value);
                                        setEndpointId("");
                                        setEndpointEntryManual(false);
                                    }} />
                                    <Field label="Endpoint" error={endpointError || null} hint={endpointHint}>
                                        {/* A select rather than a datalist: browsers disagree about
                                            whether a datalist shows an option's label at all, and
                                            Firefox shows only the value, so the hostname could go
                                            missing on the control whose whole point is showing it. */}
                                        <div aria-busy={endpointLoading}>
                                            <Select
                                                ariaLabel="Endpoint"
                                                disabled={endpointLoading}
                                                value={endpointPickerValue}
                                                placeholder={endpointLoading ? "Searching endpoints…" : "Select endpoint"}
                                                onChange={(next) => {
                                                    setEndpointEntryManual(next === MANUAL_ENDPOINT_ENTRY);
                                                    setEndpointId(next === MANUAL_ENDPOINT_ENTRY ? "" : next);
                                                }}
                                                options={[
                                                    ...endpointOptions.items.map((endpoint) => ({
                                                        value: endpoint.id,
                                                        label: endpoint.hostname ? `${endpoint.hostname} (${endpoint.id})` : endpoint.id,
                                                    })),
                                                    { value: MANUAL_ENDPOINT_ENTRY, label: "Enter an ID manually..." },
                                                ]}
                                            />
                                        </div>
                                        {endpointEntryManual && (
                                            <input
                                                className={inputClass}
                                                value={endpointId}
                                                onChange={(event) => setEndpointId(event.target.value)}
                                                placeholder="Endpoint UUID"
                                            />
                                        )}
                                    </Field>
                                    <div className="grid grid-cols-2 gap-[10px]">
                                        <Field label="Track">
                                            <Select ariaLabel="Track" value={trackId} onChange={setTrackId} placeholder="Select track" options={trackOptions} />
                                        </Field>
                                        <Field label="Wave number" error={wavePositionValid ? null : wavePositionError}>
                                            <input className={inputClass} type="number" min="1" max={MAX_WAVE_POSITION} step="1" value={wavePosition} onChange={(event) => setWavePosition(event.target.value)} />
                                        </Field>
                                    </div>
                                    <div className="flex flex-wrap justify-end gap-[8px]">
                                        <Button disabled={!endpointId || !trackId} onClick={() => void setEndpointTrack()}>Assign track</Button>
                                        <Button disabled={!endpointId || !wavePositionValid} onClick={() => void setEndpointWave()}>Assign wave</Button>
                                    </div>
                                </Panel>

                                <Panel title="Directory group override" className="content-start">
                                    <p className={PANEL_TEXT}>
                                        The same override for everyone in a directory group, so membership decides
                                        rollout order and you do not maintain a list of machines. Groups come from
                                        your synced directories, and a rule follows the group: endpoints joining or
                                        leaving pick it up on the next sync. As with an endpoint override, the
                                        track and wave apply to every component.
                                    </p>
                                    {directoryUnavailable || integrations.length === 0 ? (
                                        <p className={PANEL_TEXT}>
                                            {directoryUnavailable
                                                ? "Directory integrations could not be loaded, so groups are unavailable here."
                                                : "No directory integration is connected, so there are no synced groups to target."}
                                        </p>
                                    ) : (
                                        <>
                                            <div className="grid grid-cols-2 gap-[10px]">
                                                <Field label="Track">
                                                    <Select ariaLabel="Group track" value={groupTrackId} onChange={setGroupTrackId} placeholder="Select track" options={trackOptions} />
                                                </Field>
                                                <Field label="Wave number" error={groupWavePositionValid ? null : wavePositionError}>
                                                    <input className={inputClass} type="number" min="1" max={MAX_WAVE_POSITION} step="1" aria-label="Group wave number" value={groupWavePosition} onChange={(event) => setGroupWavePosition(event.target.value)} />
                                                </Field>
                                            </div>
                                            <Field label="Directory">
                                                <Select
                                                    ariaLabel="Directory"
                                                    value={directoryIntegrationId}
                                                    placeholder="Select directory"
                                                    onChange={(next) => {
                                                        setDirectoryIntegrationId(next);
                                                        setDirectoryGroupId("");
                                                    }}
                                                    options={integrations.map((integration) => ({
                                                        value: integration.id,
                                                        label: integration.displayName || integration.provider || integration.id,
                                                    }))}
                                                />
                                            </Field>
                                            <Field label="Group" hint={groups.truncated ? `Showing the first ${DIRECTORY_GROUP_PAGE_SIZE} groups.` : null}>
                                                <Select
                                                    ariaLabel="Group"
                                                    value={directoryGroupId}
                                                    disabled={!directoryIntegrationId}
                                                    placeholder={directoryIntegrationId ? "Select group" : "Select a directory first"}
                                                    onChange={setDirectoryGroupId}
                                                    options={groups.items.map((group) => ({ value: group.sourceId, label: group.displayName || group.sourceId }))}
                                                />
                                            </Field>
                                        </>
                                    )}
                                    <div className="flex flex-wrap justify-end gap-[8px]">
                                        <Button disabled={!directoryIntegrationId || !directoryGroupId || !groupTrackId} onClick={() => void setDirectoryGroupTrack()}>Assign track</Button>
                                        <Button disabled={!directoryIntegrationId || !directoryGroupId || !groupWavePositionValid} onClick={() => void setDirectoryGroupWave()}>Assign wave</Button>
                                    </div>
                                </Panel>
                            </div>

                            {(data.endpointRules.length > 0 || data.directoryGroupRules.length > 0) && (
                                <div className="grid min-w-0 gap-[10px] lg:grid-cols-2">
                                    <Panel title="Endpoint rules" className="content-start">
                                        {data.endpointRules.length === 0 ? <p className={PANEL_TEXT}>No endpoint overrides.</p> : null}
                                        <ul className="m-0 grid list-none gap-[2px] p-0 empty:hidden">
                                            {data.endpointRules.map((rule) => {
                                                const id = protoBytesToUuid(rule.endpointId);
                                                return (
                                                    <li key={rule.endpointId} className="flex flex-wrap items-center justify-between gap-x-[12px] gap-y-[4px] rounded-[6px] px-[8px] py-[6px] hover:bg-[color:var(--control-bg)]">
                                                        <span className="grid min-w-0 gap-[1px]">
                                                            <span className="break-all text-[12.5px] text-[color:var(--ink)]" title={id}>{rule.hostname || id}</span>
                                                            <span className="break-all font-mono text-[11.5px] text-[color:var(--muted)]">Track {protoBytesToUuid(rule.trackId) || "default"}, wave {rule.wavePosition || "adaptive"}</span>
                                                        </span>
                                                        {canControl && <Button variant="danger-ghost" size="sm" disabled={saving} aria-label={`Remove endpoint override for ${rule.hostname || id}`} onClick={() => setAsking({ kind: "endpoint", rule })}>Remove override</Button>}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </Panel>
                                    <Panel title="Directory group rules" className="content-start">
                                        {data.directoryGroupRules.length === 0 ? <p className={PANEL_TEXT}>No directory group overrides.</p> : null}
                                        <ul className="m-0 grid list-none gap-[2px] p-0 empty:hidden">
                                            {data.directoryGroupRules.map((rule) => (
                                                <li key={`${rule.directoryIntegrationId}:${rule.directoryGroupId}`} className="flex flex-wrap items-center justify-between gap-x-[12px] gap-y-[4px] rounded-[6px] px-[8px] py-[6px] hover:bg-[color:var(--control-bg)]">
                                                    <span className="grid min-w-0 gap-[1px]">
                                                        <span className="break-all text-[12.5px] text-[color:var(--ink)]">{directoryGroupLabel(rule)}</span>
                                                        <span className="break-all font-mono text-[11.5px] text-[color:var(--muted)]">Track {protoBytesToUuid(rule.trackId) || "default"}, wave {rule.wavePosition || "adaptive"}</span>
                                                    </span>
                                                    {canControl && <Button variant="danger-ghost" size="sm" disabled={saving} aria-label={`Remove directory override for ${directoryGroupLabel(rule)}`} onClick={() => setAsking({ kind: "group", rule })}>Remove override</Button>}
                                                </li>
                                            ))}
                                        </ul>
                                        {data.assignmentRulesTruncated && <p className={PANEL_TEXT}>Only the first 500 endpoint and group rules are shown.</p>}
                                    </Panel>
                                </div>
                            )}
                        </div>
                    )}

                    {tab === TAB_COMPONENTS && canControl && (
                        <div className="grid max-w-[640px] gap-[32px]">
                            <Field label="Component" hint="Release policy and preview below apply to this component. Your wave order and overrides are not affected by this choice.">
                                <div className="max-w-[420px]">
                                    <Select
                                        ariaLabel="Component"
                                        value={componentId}
                                        onChange={setComponentId}
                                        placeholder="Select component"
                                        options={components.map(([id, name]) => ({ value: id, label: name }))}
                                    />
                                </div>
                            </Field>

                            <section className="grid gap-[14px]">
                                <div className="grid gap-[4px]">
                                    <h3 className={PAGE_PART}>Release policy</h3>
                                    <p className={DESCRIPTION}>
                                        What happens when Origin finishes rolling a release out to the fleet. On automatic,
                                        it starts moving through your endpoints on its own. On manual, it waits here until
                                        you start it. Major releases are asked separately because they are the ones most
                                        worth holding.
                                    </p>
                                </div>
                                <div className="grid gap-[12px] sm:grid-cols-2">
                                    <Field label="Minor releases">
                                        <Select ariaLabel="Minor releases" value={minorPolicy} onChange={setMinorPolicy} placeholder="Mixed settings - choose a shared policy" options={POLICY_OPTIONS} />
                                    </Field>
                                    <Field label="Major releases">
                                        <Select ariaLabel="Major releases" value={majorPolicy} onChange={setMajorPolicy} placeholder="Mixed settings - choose a shared policy" options={POLICY_OPTIONS} />
                                    </Field>
                                </div>
                                {componentId === CONTENT_UPDATES && <p className="m-0 text-[12px] leading-[1.4] text-[color:var(--muted)]">Applies to all {contentComponentIds.length} listed content modules. New versions keep these settings.</p>}
                                <div className="flex justify-end">
                                    <Button variant="primary" disabled={!componentId || !majorPolicy || !minorPolicy} onClick={() => void setPolicy()}>Save policy</Button>
                                </div>
                            </section>

                            <section className="grid gap-[14px]">
                                <div className="grid gap-[6px]">
                                    <h3 className={PAGE_PART}>Tracks</h3>
                                    <p className={DESCRIPTION}>Your endpoints sit on one of two tracks.</p>
                                    <dl className="m-0 grid max-w-[72ch] gap-[6px] text-[13px] leading-[1.5]">
                                        <div>
                                            <dt className="inline font-medium text-[color:var(--ink)]">Main.</dt>
                                            <dd className="m-0 inline text-[color:var(--muted)]">
                                                {" "}Where every endpoint is unless you move it. Releases arrive here under
                                                the policy above, then spread across these endpoints wave by wave.
                                            </dd>
                                        </div>
                                        <div>
                                            <dt className="inline font-medium text-[color:var(--ink)]">Preview.</dt>
                                            <dd className="m-0 inline text-[color:var(--muted)]">
                                                {" "}An opt-in group that takes a release ahead of main, so you can check it
                                                against your own software first. Everyone on it gets the release at once,
                                                with no waves. It is early relative to your other endpoints, not to Origin:
                                                a release only reaches preview after Origin has finished rolling it out
                                                across the fleet.
                                            </dd>
                                        </div>
                                    </dl>
                                    <p className={DESCRIPTION}>
                                        Preview has to be turned on per component before endpoints on it receive that
                                        component early. Move endpoints onto it with an override in Your fleet.
                                    </p>
                                </div>
                                {previewTrack ? (
                                    <div className="flex flex-wrap items-center gap-[12px]">
                                        <Button disabled={!componentId} onClick={togglePreview}>
                                            {previewEnabled ? "Disable preview" : "Enable preview"}
                                        </Button>
                                        <span className="text-[12.5px] text-[color:var(--muted)]">
                                            {!componentId
                                                ? "Select a component to see whether preview is on."
                                                : previewMixed
                                                    ? "On for some content updates. Enable preview to apply it to all."
                                                    : previewEnabled
                                                    ? "On for this component."
                                                    : "Off for this component."}
                                        </span>
                                    </div>
                                ) : (
                                    <p className="m-0 text-[12.5px] text-[color:var(--muted)]">
                                        This tenant has no preview track.
                                    </p>
                                )}
                            </section>
                        </div>
                    )}
                </fieldset>
            </div>

            <ConfirmDialog
                open={pendingConfirm != null}
                analyticsId={DIALOG.ROLLOUT_WAVE_CONFIRM}
                eyebrow="Wave schedule"
                title={pendingConfirm?.kind === "clear"
                    ? "Go back to Origin's default schedules?"
                    : "Put every component on this schedule?"}
                body={pendingConfirm ? (
                    <div className="grid gap-[10px]">
                        <p className="m-0 text-[13px] leading-[1.5] text-[color:var(--muted)]">
                            {pendingConfirm.kind === "clear"
                                ? `${plural(pendingConfirm.moved.length, "component")} currently on your schedule ${pendingConfirm.moved.length === 1 ? "goes" : "go"} back to the default Origin ships for it, which differs by component:`
                                : `${plural(pendingConfirm.moved.length, "component")} on a different schedule today ${pendingConfirm.moved.length === 1 ? "moves" : "move"} to this one:`}
                        </p>
                        <ul className="m-0 grid list-none gap-[2px] p-0 text-[13px] text-[color:var(--ink)]">
                            {pendingConfirm.moved.map((name) => <li key={name}>{name}</li>)}
                        </ul>
                        <p className="m-0 text-[13px] leading-[1.5] text-[color:var(--muted)]">
                            {pendingConfirm.kind === "clear"
                                ? "A component you gave its own schedule keeps it. Releases already moving keep the schedule they started on."
                                : "Releases already moving keep the schedule they started on."}
                        </p>
                    </div>
                ) : null}
                confirmLabel={pendingConfirm?.kind === "clear" ? "Use Origin's defaults" : "Save schedule"}
                destructive={pendingConfirm?.kind === "clear"}
                confirmDisabled={saving}
                onConfirm={confirmPendingAction}
                onCancel={() => setPendingConfirm(null)}
            />

            <ConfirmDialog
                open={asking?.kind === "preview"}
                analyticsId={DIALOG.ROLLOUT_ACTION_CONFIRM}
                eyebrow="Preview track"
                title="Disable preview for this component?"
                body="Installed software is not downgraded; endpoints may remain ahead of main until main catches up."
                confirmLabel="Disable preview"
                cancelLabel="Keep preview"
                confirmDisabled={saving}
                onConfirm={() => setPreview(false)}
                onCancel={() => setAsking(null)}
            />

            <ConfirmDialog
                open={removingOverride != null}
                analyticsId={DIALOG.ROLLOUT_OVERRIDE_REMOVE}
                eyebrow="Override"
                title={removingOverride ? `Remove the track and wave overrides for ${overrideName(removingOverride)}?` : ""}
                body={removingOverride?.kind === "endpoint"
                    ? "Applicable group rules and default rollout placement will take over."
                    : "Other applicable rules and default rollout placement will take over."}
                confirmLabel="Remove override"
                cancelLabel="Keep override"
                destructive
                confirmDisabled={saving}
                onConfirm={removeOverride}
                onCancel={() => setAsking(null)}
            />
        </>
    );
}
